/**
 * Events sent from the Premiere UXP host to the Air WebView app
 */
const _PREMIERE_TO_AIR_EVENT = {
  /** Active sequence info response */
  ActiveSequence: 'premiere-to-air:active-sequence',
  /** Chapter markers were created on the timeline */
  ChaptersCreated: 'premiere-to-air:chapters-created',
  /** Export rendering completed */
  ExportComplete: 'premiere-to-air:export-complete',
  /** List of available .epr export presets enumerated from disk */
  ExportPresets: 'premiere-to-air:export-presets',
  /** Export progress update */
  ExportProgress: 'premiere-to-air:export-progress',
  /** Sequence was queued for export */
  ExportQueued: 'premiere-to-air:export-queued',
  /** Host environment info (name + version) emitted immediately after Ready */
  HostInfo: 'premiere-to-air:host-info',
  /** Batch asset import completed */
  ImportBatchComplete: 'premiere-to-air:import-batch-complete',
  /** Asset import completed */
  ImportComplete: 'premiere-to-air:import-complete',
  /** Asset import download progress */
  ImportProgress: 'premiere-to-air:import-progress',
  /** Timeline markers were created */
  MarkersCreated: 'premiere-to-air:markers-created',
  /** Project bin create/reuse response */
  ProjectBinCreated: 'premiere-to-air:project-bin-created',
  /** UXP host is ready */
  Ready: 'premiere-to-air:ready',
  /** Premiere UI theme changed — host reduces the 5 Premiere themes to Air's 2-mode design system */
  ThemeChanged: 'premiere-to-air:theme-changed',
  /** Transcript import into Premiere Text panel completed */
  TranscriptImported: 'premiere-to-air:transcript-imported',
  /** Upload to Air completed */
  UploadComplete: 'premiere-to-air:upload-complete',
  /** Upload to Air progress */
  UploadProgress: 'premiere-to-air:upload-progress',
} as const;

/** @public */
export const PremiereToAirEvent = _PREMIERE_TO_AIR_EVENT;

/** @public */
export type PremiereToAirEvent = (typeof PremiereToAirEvent)[keyof typeof PremiereToAirEvent];

/**
 * Message: Asset import completed
 * @public
 */
export type PremiereToAirImportComplete = {
  type: typeof PremiereToAirEvent.ImportComplete;
  data: {
    success: boolean;
    error?: string;
    /** File name of the completed import — used to clear per-file progress tracking */
    fileName?: string;
  };
};

/**
 * Message: Asset import download progress
 * @public
 */
export type PremiereToAirImportProgress = {
  type: typeof PremiereToAirEvent.ImportProgress;
  data: {
    /** Bytes received so far */
    bytes: number;
    fileName: string;
  };
};

/**
 * Message: Batch asset import completed
 * @public
 */
export type PremiereToAirImportBatchComplete = {
  type: typeof PremiereToAirEvent.ImportBatchComplete;
  data: {
    batchId: string;
    /** File names Premiere accepted into the project */
    importedFileNames: string[];
    /** Per-file download failures — these never reached importFiles */
    downloadFailures: Array<{ error: string; fileName: string }>;
    /** Set when importFiles rejects the batch after downloads succeeded */
    importError?: string;
  };
};

/**
 * Message: Timeline markers were created
 * @public
 */
export type PremiereToAirMarkersCreated = {
  type: typeof PremiereToAirEvent.MarkersCreated;
  data: {
    count: number;
    error?: string;
  };
};

/**
 * Message: Chapter markers were created on the timeline
 * @public
 */
export type PremiereToAirChaptersCreated = {
  type: typeof PremiereToAirEvent.ChaptersCreated;
  data: {
    count: number;
    error?: string;
  };
};

/**
 * Message: Sequence was queued for export
 * @public
 */
export type PremiereToAirExportQueued = {
  type: typeof PremiereToAirEvent.ExportQueued;
  data: {
    success: boolean;
    error?: string;
  };
};

/**
 * Message: Export progress update
 * @public
 */
export type PremiereToAirExportProgress = {
  type: typeof PremiereToAirEvent.ExportProgress;
  data: {
    /** 0-100 percentage */
    progress: number;
  };
};

/**
 * Message: Export rendering completed
 * @public
 */
export type PremiereToAirExportComplete = {
  type: typeof PremiereToAirEvent.ExportComplete;
  data: {
    success: boolean;
    /** Local file path of the rendered output */
    filePath?: string;
    error?: string;
  };
};

/**
 * Message: Upload to Air progress
 * @public
 */
export type PremiereToAirUploadProgress = {
  type: typeof PremiereToAirEvent.UploadProgress;
  data: {
    /** 0-100 percentage */
    progress: number;
  };
};

/**
 * Message: Upload to Air completed
 * @public
 */
export type PremiereToAirUploadComplete = {
  type: typeof PremiereToAirEvent.UploadComplete;
  data: {
    success: boolean;
    /** The clip ID of the uploaded asset */
    clipId?: string;
    assetId?: string;
    error?: string;
  };
};

/**
 * A single discovered `.epr` export preset. `name` is the display label
 * (filename with `.epr` stripped); `path` is the native filesystem path
 * passed back through to `EncoderManager.exportSequence`.
 * @public
 */
export type PremiereExportPreset = {
  name: string;
  path: string;
};

/**
 * Message: List of available `.epr` export presets enumerated from Adobe's
 * preset directories. Empty array is a valid response (means no presets
 * were found anywhere on disk).
 * @public
 */
export type PremiereToAirExportPresets = {
  type: typeof PremiereToAirEvent.ExportPresets;
  data: {
    presets: PremiereExportPreset[];
  };
};

/**
 * Message: Active sequence info response
 * @public
 */
export type PremiereToAirActiveSequence = {
  type: typeof PremiereToAirEvent.ActiveSequence;
  data: {
    name: string;
    /** Duration in seconds */
    duration: number;
    /** Ticks per second for timecode conversion */
    ticksPerSecond: number;
  } | null;
};

/**
 * Message: Project bin was created or reused for batch import
 * @public
 */
export type PremiereToAirProjectBinCreated = {
  type: typeof PremiereToAirEvent.ProjectBinCreated;
  data: {
    binKey: string;
    error?: string;
    success: boolean;
  };
};

/**
 * Message: UXP host is ready
 * @public
 */
export type PremiereToAirReady = {
  type: typeof PremiereToAirEvent.Ready;
};

/**
 * Message: Host-environment info (`require('uxp').host`). Emitted by the
 * host immediately after `Ready` so the WebView can pick a variant-aware
 * deep-link URL scheme (`adobe+ppro://` for Premiere Pro, `adobe+pprobeta://`
 * for Premiere Pro Beta) on the post-auth callback page. New event (not a
 * mutation of `Ready`) per `plugins/premiere/CLAUDE.md`'s frozen-contract
 * rule "New events, not mutated payloads."
 * @public
 */
export type PremiereToAirHostInfo = {
  type: typeof PremiereToAirEvent.HostInfo;
  data: {
    name: string;
    version: string;
  };
};

/**
 * Message: Premiere UI theme changed. Host reduces the 5 Premiere themes
 * (Lightest, Light, Medium, Dark, Darkest) to Air's 2-mode design system
 * (Lightest + Light → `light`; Medium + Dark + Darkest → `dark`) so the
 * WebView doesn't have to know about Premiere-specific theme names.
 * @public
 */
export type PremiereToAirThemeChanged = {
  type: typeof PremiereToAirEvent.ThemeChanged;
  data: {
    theme: 'light' | 'dark';
  };
};

/**
 * Message: Transcript import into Premiere Text panel completed
 * @public
 */
export type PremiereToAirTranscriptImported = {
  type: typeof PremiereToAirEvent.TranscriptImported;
  data: {
    success: boolean;
    error?: string;
  };
};

/**
 * Union type of all messages from Premiere UXP host to Air WebView
 * @public
 */
export type PremiereToAirMessage =
  | PremiereToAirImportComplete
  | PremiereToAirImportBatchComplete
  | PremiereToAirImportProgress
  | PremiereToAirMarkersCreated
  | PremiereToAirChaptersCreated
  | PremiereToAirExportQueued
  | PremiereToAirExportProgress
  | PremiereToAirExportComplete
  | PremiereToAirExportPresets
  | PremiereToAirUploadProgress
  | PremiereToAirUploadComplete
  | PremiereToAirActiveSequence
  | PremiereToAirReady
  | PremiereToAirHostInfo
  | PremiereToAirProjectBinCreated
  | PremiereToAirThemeChanged
  | PremiereToAirTranscriptImported;
